import { PrintTemplateType } from "@shared/enums/print-template-type";
import { PosLocation } from "src/app/point-of-sale/models/pos-location";
import { AccountsTypes } from "./account";
import { CashTransactionLog } from "./cash-transaction";
import { CustomerV3 } from "./customer-v3";
import { Metadata } from "./metadata";
import { INativeServiceSyncable } from "./native-service-syncable";
import { PaymentType } from "./payment-type";
import { PrintTemplateV3 } from "./print-template-v3";
import { IReplicableEntity } from "./replicable-entity";
import { OrderLayout } from "src/app/point-of-sale/enums/order-layout";
import { CardLayout } from "src/app/point-of-sale/models/product-card-layout.model";
import { CashRegisterClosurePostingStatus } from "src/app/point-of-sale/enums/cash-register-closure-posting-status";

/** Backend `DeviceIdSource` (`device_id_source`): which identity a register pairs with. */
export enum DeviceIdSource {
    Web = 1,
    Desktop = 2
}

/** The stored source decides. A copy without one predates the field (the backend migrated those to Desktop), so it is a Desktop register. */
export function deviceIdSourceOf(cashRegister: Pick<CashRegister, 'device_id_source'> | null | undefined): DeviceIdSource {
    return cashRegister?.device_id_source === DeviceIdSource.Web ? DeviceIdSource.Web : DeviceIdSource.Desktop;
}

/** The prefix is display only — the stored id is the raw Known Devices or physical id. Only the last 4 characters show. */
export function maskDeviceId(deviceId: string | null | undefined, source: DeviceIdSource): string {
    if (!deviceId) return '';
    return `${source === DeviceIdSource.Web ? 'WEB' : 'Desktop'}-••••${deviceId.slice(-4)}`;
}

export class CashRegister implements IReplicableEntity {
    id!: string;
    code!: string;
    name!: string;
    device_id?: string;
    device_id_source?: DeviceIdSource;
    activated_user?: string;
    allow_open_close!: boolean;
    location?: PosLocation;
    customer?: CustomerV3;
    status?: CashRegisterStatus;
    opening_details?: CashRegisterOpening;
    closing_details?: CashRegisterClosing;
    defaultPrintTemplate?: PrintTemplateType;
    printTemplates: PrintTemplateV3[] = [];
    paymentTypes: CashRegisterPaymentType[] = [];
    orderLayout: OrderLayout = OrderLayout.Cards;
    cardLayout?: CardLayout;
    active: boolean = true;
    metadata?: Metadata;

    public static clone(cashRegister: CashRegister): CashRegister | undefined {
        if (!cashRegister)
            return undefined;

        const newCashRegister: CashRegister = Object.assign(new CashRegister(), cashRegister);
        newCashRegister.closing_details = CashRegisterClosing.clone(cashRegister.closing_details);
        return newCashRegister;
    }

    public setDefaultPaymentType(paymentType: PaymentType) {
        this.clearDefaultPaymentType();

        if (paymentType) {
            const newDefaultPaymentType = this.paymentTypes?.find(itm => itm.paymentType.id === paymentType.id);
            if (newDefaultPaymentType) {
                newDefaultPaymentType.isDefault = true;
            }
        }
    }

    public clearDefaultPaymentType() {
        const defaultPaymentType = this.paymentTypes?.find(itm => itm.isDefault);
        if (defaultPaymentType) {
            defaultPaymentType.isDefault = false;
        }
    }
}

export interface DevicePairing {
    id: string;
    device_id: string;
}

export enum CashRegisterStatus {
    Open = 1,
    Closed = 2
}

export class CashRegisterOpening {
    id?: string;
    opening_amount!: number;
    opening_date!: Date;
    opened_by!: string;
    syncMetadata?: INativeServiceSyncable;
}

export class CashRegisterClosing {
    id?: string;
    code!: string;
    closing_amount!: number;
    expected_cash_payments!: CashPaymentBreakdown;
    actual_cash_payments!: CashPaymentBreakdown;
    closing_date!: Date;
    closed_by!: string;
    syncMetadata?: INativeServiceSyncable;
    posting_status?: CashRegisterClosurePostingStatus;
    reference?: string[];

    public static clone(cashRegisterClosing: CashRegisterClosing | undefined): CashRegisterClosing | undefined {
        if (!cashRegisterClosing)
            return undefined;

        const newCashRegisterClosing = Object.assign(new CashRegisterClosing(), cashRegisterClosing);
        newCashRegisterClosing.expected_cash_payments = Object.assign(new CashPaymentBreakdown(), cashRegisterClosing.expected_cash_payments);
        newCashRegisterClosing.actual_cash_payments = Object.assign(new CashPaymentBreakdown(), cashRegisterClosing.actual_cash_payments);
        return newCashRegisterClosing;
    }
}

export class CashPaymentBreakdown {
    cash_sales_payments: CashPayment[] = [];
    on_account_amount?: number = 0;
    cash_in_amount?: number = 0;
    cash_out_amount?: number = 0;

    get cashSales(): number {
        const cashSales = this.cash_sales_payments.filter(element => element.payment_type_name === AccountsTypes[AccountsTypes.Cash]);
        if (!cashSales.length) return 0;

        return cashSales.map(item => item.amount)
            .reduce((a, b) => a + b);
    }

    get bankSales(): number {
        const bankSales = this.cash_sales_payments.filter(element => element.payment_type_name === AccountsTypes[AccountsTypes.Bank] || element.payment_type_name === 'Card');
        if (!bankSales.length) return 0;

        return bankSales.map(item => item.amount)
            .reduce((a, b) => a + b);
    }

    get totalCashSalesPayments(): number {
        if (!this.cash_sales_payments?.length)
            return 0;

        return this.cash_sales_payments.map(item => item.amount).reduce((a, b) => a + b, 0);
    }

    get totalAmounts(): number {
        return this.totalCashSalesPayments +
            (this.cash_in_amount ?? 0) -
            (this.cash_out_amount ?? 0);
    }
}

export class CashPayment {
    account_id?: number;
    account_name?: string;
    account_type?: string;
    payment_type_name?: string;
    amount: number = 0;
}

export class SyncCashRegisterUpdates {
    id?: string;
    cashRegisterStatusLogs: CashRegisterStatusLog[] = [];
    cashTransactionLogs: CashTransactionLog[] = [];
}

export class CashRegisterStatusLog {
    status!: CashRegisterStatus;
    changeDate!: Date;
    changedBy!: string;
    openingDetails?: CashRegisterOpening;
    closingDetails?: CashRegisterClosing;
}

export class CashRegisterPaymentType {
    id?: string;
    paymentType!: PaymentType;
    isDefault?: boolean;
}
